import React from 'react';
import { VISIONTRAVEL, SAFEGUARD, REALVISION, EXPANSION } from '../../../images/imagePath';
import { BusinessHook } from '../../../hooks';

/**
 * P01 · "Explora por línea de negocio" (mockup líneas 895-937).
 * Grid de 4 cards con foto + máscara + acento inferior + título/subtítulo.
 * Los colores del acento inferior siguen los del mockup por línea de negocio.
 *
 * Las imágenes salen de `Negocios.UrlImagen` (DB) con fallback al asset
 * empaquetado — así se pueden cambiar sin redeploy. tagline y accent
 * siguen locales (no están en la tabla) y se emparejan por nombre.
 */
const BUSINESSES = [
  {
    key: 'travel',
    name: 'Vision Travel',
    tagline: 'Turismo y viajes',
    accent: '#1FAED5',
    image: VISIONTRAVEL,
  },
  {
    key: 'safeguard',
    name: 'Safeguard',
    tagline: 'Seguros y protección',
    accent: '#7D97D9',
    image: SAFEGUARD,
  },
  {
    key: 'real-vision',
    name: 'Real Vision',
    tagline: 'Bienes raíces',
    accent: '#EC7137',
    image: REALVISION,
  },
  {
    key: 'visionbiz',
    name: 'VisionBiz',
    tagline: 'Negocio y expansión',
    accent: '#59A8D6',
    image: EXPANSION,
  },
];

/* Normaliza el nombre para matchear la fila de BBDD con el entry
   hardcoded: minúsculas, sin tildes ni espacios extra. Así "Vision
   Travel" matchea "visión travel", "VISION TRAVEL", etc. */
const normalize = (s) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    // Rango de combining diacritical marks (U+0300–U+036F) — quita los
    // acentos que NFD acaba de separar de la vocal base.
    .replace(/[̀-ͯ]/g, '');

export default function HomeBusinessGrid({ onSelect }) {
  /* URLs de BBDD indexadas por nombre normalizado. Si Negocios.UrlImagen
     está vacío (o la fila aún no fue backfilled), caemos al asset local
     empaquetado — sin regresión visual. */
  const { data: businesses } = BusinessHook.useGetAll();
  const businessByName = React.useMemo(() => {
    const m = new Map();
    (businesses ?? []).forEach((b) => m.set(normalize(b.name), b));
    return m;
  }, [businesses]);
  const urlByName = React.useMemo(() => {
    const m = new Map();
    (businesses ?? []).forEach((b) => {
      if (b?.imageUrl) m.set(normalize(b.name), b.imageUrl);
    });
    return m;
  }, [businesses]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14,
        marginBottom: 20,
      }}
      data-slot="home-business-grid"
    >
      {BUSINESSES.map((biz) => {
        /* Resolvemos el id del negocio en BBDD por nombre normalizado,
           para que al navegar a /courses podamos pre-aplicar el filtro
           por línea de negocio igual que el chip del sidebar. Si el
           negocio aún no está en la respuesta de /business/all (fetch
           pendiente o fila faltante), onSelect recibe `null` como id y
           /courses abre sin filtro — mejor que no abrir nada. */
        const resolvedBiz = businessByName.get(normalize(biz.name));
        return (
        <button
          key={biz.key}
          type="button"
          onClick={() =>
            onSelect?.({
              key: biz.key,
              name: biz.name,
              id: resolvedBiz?.id ?? null,
            })
          }
          className="text-left cursor-pointer"
          style={{
            position: 'relative',
            display: 'block',
            padding: 0,
            border: 0,
            borderRadius: 18,
            overflow: 'hidden',
            background: 'var(--color-ink)',
            aspectRatio: '4 / 3',
          }}
        >
          <img
            src={urlByName.get(normalize(biz.name)) || biz.image}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(11,20,36,.97) 0%, rgba(11,20,36,.95) 34%, rgba(11,20,36,.74) 55%, rgba(11,20,36,.2) 82%, rgba(11,20,36,0) 100%)',
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 5,
              background: biz.accent,
            }}
          />
          <span
            style={{ position: 'absolute', left: 14, right: 14, bottom: 16 }}
          >
            <span
              style={{
                display: 'block',
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-.015em',
                color: '#fff',
                marginBottom: 3,
              }}
            >
              {biz.name}
            </span>
            <span
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 700,
                color: '#DCE4F0',
              }}
            >
              {biz.tagline}
            </span>
          </span>
        </button>
        );
      })}
    </div>
  );
}
